import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../../appConfig.service';
import { HttpInternalService } from '../../data/service/http.service';
import { OrganizationService } from '../../data/service/organization.service';
import { GodModeService } from '../../data/service/god-mode.service';
import { legacyFallbackFor } from '../config/nav-items.config';
import { SessionService } from './session.service';
import { SharedService } from './shared.service';

export interface EntitlementState {
  modules: string[];
  planCode: string | null;
  /** SD-1229: pinned / stripe / stripe-unmapped / free / fallback; null on the fallback set. */
  planSource: string | null;
  /**
   * SD-1229: plan limits configured for the organisation, by code (null = unlimited). A limit
   * absent here is governed by its legacy allowance, which the usage endpoints report.
   */
  limits: { [code: string]: number | null };
  /**
   * True when the set came from LEGACY_FALLBACK rather than from the server -- whether that
   * was deliberate (the feature flag is off) or a failure (unreachable, 500, `degraded`).
   *
   * This is the flag the sidemenu needs, and it is deliberately not `degraded`: the
   * flag-off path is a fallback but is *not* degraded, so the two are not interchangeable.
   * Only a server-resolved set has had AllowedRoleMask applied to it, so only a
   * server-resolved set may skip the client's own role gate.
   */
  fromFallback: boolean;
  /** True only when the fallback was reached by failure, not by the flag being off. */
  degraded: boolean;
  version: number;
}

/**
 * SD-1218: which modules the current user may see, in the current organisation.
 *
 * The server answers with module codes only; everything about how those codes are rendered
 * lives in nav-items.config.ts. The result is cached with `shareReplay(1)` and re-fetched
 * only when something that could change the answer happens.
 *
 * The invariant this service exists to protect: **no failure path may produce an empty
 * module list.** An empty sidebar is indistinguishable from a broken app, and a user who
 * loses their navigation cannot even reach Settings to sign out. So a missing config flag,
 * an unreachable endpoint, a 500, a `degraded: true` response and an unknown role all fall
 * back to LEGACY_FALLBACK -- the exact menu the hardcoded template produced before SD-1218.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementsService {

  private _refresh$ = new BehaviorSubject<number>(0);

  readonly state$: Observable<EntitlementState> = combineLatest([
    this._refresh$,
    // Emits on organisation switch. The answer is org-scoped -- a user can be an Admin in
    // one organisation and a Submitter in another -- so switching must re-resolve.
    this.organizationService.selectedOrganizationId,
    // Emits on entering and leaving god mode. Impersonation issues the impersonated user's
    // token, so the cached set belongs to the wrong person until this fires.
    this.godMode.godModeUser$
  ]).pipe(
    switchMap(() => this.fetch()),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  readonly modules$: Observable<string[]> = this.state$.pipe(map(state => state.modules));

  constructor(
    private http: HttpInternalService,
    private appConfig: AppConfigService,
    private session: SessionService,
    private organizationService: OrganizationService,
    private godMode: GodModeService,
    private sharedService: SharedService
  ) {
    // The sidebar already had a "something changed, redraw yourself" signal. Reusing it means
    // existing callers of triggerSidebarReload() keep working and the sidebar no longer needs
    // its own subscription.
    this.sharedService.reloadSidebar$.subscribe(() => this.refresh());
  }

  /** Whether one module is entitled. Safe to use with the async pipe. */
  has$(code: string): Observable<boolean> {
    return this.state$.pipe(
      map(state => state.modules.indexOf(code) >= 0),
      distinctUntilChanged()
    );
  }

  /**
   * SD-1229: a configured plan limit. `undefined` when none is configured (the legacy
   * allowance applies), `null` for unlimited.
   */
  limit$(code: string): Observable<number | null | undefined> {
    return this.state$.pipe(
      map(state => (state.limits && code in state.limits) ? state.limits[code] : undefined),
      distinctUntilChanged()
    );
  }

  refresh(): void {
    this._refresh$.next(this._refresh$.value + 1);
  }

  private fetch(): Observable<EntitlementState> {
    // Flag off: behave exactly as the app did before SD-1218, and make no request at all.
    if (!this.isEnabled()) {
      return this.fallback(false);
    }

    const organizationId = this.organizationService.selectedOrganization;
    const params: any = {};
    if (organizationId) {
      params.organizationId = organizationId;
    }

    return this.http.getRequest<any>('Modules/Me', params).pipe(
      switchMap(res => {
        // `degraded` means the server could not resolve the caller's organisation or role,
        // or its registry is unseeded. Either way its answer is not one to hide a menu on.
        if (!res || res.degraded === true) {
          return this.fallback(true);
        }

        return of({
          modules: res.modules || [],
          planCode: res.planCode || null,
          planSource: res.planSource || null,
          limits: res.limits || {},
          fromFallback: false,
          degraded: false,
          version: res.version || 0
        } as EntitlementState);
      }),
      catchError(() => this.fallback(true))
    );
  }

  private fallback(degraded: boolean): Observable<EntitlementState> {
    return this.session.role$.pipe(
      take(1),
      map(role => ({
        modules: legacyFallbackFor(role),
        planCode: null,
        planSource: null,
        limits: {},
        fromFallback: true,
        degraded: degraded,
        version: 0
      } as EntitlementState))
    );
  }

  private isEnabled(): boolean {
    try {
      return this.appConfig.moduleEntitlementsEnabled;
    } catch {
      // AppConfigService throws when the config has not loaded yet. Treat that as "off"
      // rather than letting it propagate and take the sidebar down with it.
      return false;
    }
  }
}
